import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import HtmlParser from 'react-html-parser';
import {
    settings, getImageCircularGenericNew, getProcessButtons, getError, sendMessageCloseCurrentWindow, isUndNullEmpNew, getResponse, getLanguageObj
} from '../../helpers/UtilsHelper';
import { GlobalFormats } from '../../classes/main/globalFormatClass';
import { AccountService } from '../../services/accounts/AccountService';

const { images, labels, messages, icons } = settings;

const currentMethod = 'AccountAdd - function';

export default function AccountAdd({
    $ = {},
    auth = {},
    user = {},
    setUser = () => { },
    showModal = false,
    setShowModal = () => { },
    dtId = '',
    languageObj = GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
} = {}) {
    //#region variables
    const [errors, setErrors] = useState({ userName: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [process, setProcess] = useState({ showLoading: false, save: false });
    const isMount = useRef(true);
    //#endregion variables

    //#region methods
    const setDefaultValuesAsync = async ({
        isModalClose = false,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setDefaultValuesAsync';
        try {
            setErrors({ userName: '' });
            setShowPassword(false);
            setShowModal(!isModalClose);
        } catch (ex) {
            const response = getError({ ex: ex, response: getResponse({ type: 0 }), currentMethod_: currentMethod, subMethod_: subMethod });
            console.error(response);
        }
    };

    const formDataValidate = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'formDataValidate';
        let response = getResponse({ type: 0 });
        try {
            const thisFieldRequired = messages?.thisFieldRequired?.[languageObj?.id];
            const errors_ = { userName: '' };

            if (isUndNullEmpNew({ data: user?.userName, subMethod: currentMethod })?.result) {
                errors_.userName = thisFieldRequired;
                response.isWarning = true;
            }

            setErrors(errors_);
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
        return response;
    };

    const createUser = async ({
        e = {},
        subMethod = '',
    }) => {
        const currentMethod = 'createUser';
        let response = getResponse({ type: 0 });
        try {
            e?.preventDefault();

            // Validate form data
            response = formDataValidate({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            setProcess(prev => ({ ...prev, showLoading: true, save: true }));

            // Execute decoupled network call via AccountService
            const token = auth?.account?.tokenVzLaML?.token;
            response = await AccountService.save({ account: user, token, subMethod: currentMethod });

            if (!response?.isSuccess || response?.isWarning) return;

            const userLocal = response?.result || {};
            setUser(userLocal);

            //#region update dataTable
            const userDtRowLocal = [
                getImageCircularGenericNew({
                    alt: 'accountImage',
                    src: userLocal?.pictureUrl,
                    subMethod: currentMethod
                })?.result,
                userLocal?.userName,
                `${userLocal?.firstName || ''}, ${userLocal?.lastName || ''}`,
                getProcessButtons({
                    id: userLocal?.id,
                    description: userLocal?.userName,
                    detailsShow: false,
                    detailsLabel: labels?.detail?.[languageObj?.id],
                    detailsEnabled: false,
                    editShow: true,
                    editLabel: labels?.edit?.[languageObj?.id],
                    editEnabled: true,
                    deleteShow: true,
                    deleteLabel: labels?.delete?.[languageObj?.id],
                    deleteEnabled: true,
                    processButtons: [],
                    languageId: languageObj?.id,
                    subMethod: currentMethod
                })?.result,
            ];
            $(`#${dtId}`).DataTable().row.add(userDtRowLocal).draw();
            //#endregion update dataTable

            response.msgText = `${messages?.processCompletedSuccessfully?.[languageObj?.id]}`;
        } catch (ex) {
            response = getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false, save: false }));

            if (response?.isSuccess && !response?.isWarning) {
                setUser({});
                setShowModal(false);
            }

            await sendMessageCloseCurrentWindow({
                response: response,
                auth: auth,
                languageId: languageObj?.id,
                subMethod: currentMethod
            });
        }
    };

    const setFormValue = ({ key, value }) => {
        setUser(prev => ({ ...prev, [key]: value }));
        if (key === 'userName' && value?.trim() !== '') {
            setErrors(prev => ({ ...prev, userName: '' }));
        }
    };

    useEffect(() => {
        isMount.current = true;
        setErrors({ userName: '' });
        return () => { isMount.current = false; };
    }, [showModal]);
    //#endregion methods

    return (
        <Modal id='accountAddModal'
            backdrop='static'
            onHide={() => setDefaultValuesAsync({ isModalClose: true, subMethod: 'modal-onHide' })}
            show={showModal}
            centered
            contentClassName='border-0 shadow-lg login-card'>

            <Modal.Header closeButton className='bg-white border-bottom-0 pt-4 px-4 align-items-center'>
                <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                    style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img alt='imgUser' src={images?.user} style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                </div>
                <div className='d-flex flex-column'>
                    <span className='h5 mb-0 font-weight-bold text-dark'>{labels?.account?.[languageObj?.id]}</span>
                    <div className='d-flex align-items-center mt-1'>
                        <span className='badge badge-pill py-1 px-2 text-uppercase badge-info'
                            style={settings?.html?.badges?.micro}>
                            {labels?.add?.[languageObj?.id]}
                        </span>
                    </div>
                </div>
            </Modal.Header>

            <form onSubmit={(e) => createUser({ e: e, subMethod: 'onSubmit - form' })} method='post'>
                <Modal.Body className='px-4 pt-2'>

                    {/* Username Input Field */}
                    <div className='form-group mb-4'>
                        <label className='small font-weight-bold text-uppercase form-label-uppercase mb-2'>
                            {labels?.userName?.[languageObj?.id]}
                        </label>
                        <div className='position-relative'>
                            <i className={`${icons?.fasFa?.addressCard || 'fas fa-address-card'} position-absolute input-icon-left`} />
                            <input className='form-control border-0 shadow-sm form-input-icon'
                                onChange={(e) => setFormValue({ key: 'userName', value: e.target.value })}
                                onFocus={() => setErrors(prev => ({ ...prev, userName: '' }))}
                                placeholder={labels?.userName?.[languageObj?.id]}
                                title={labels?.userName?.[languageObj?.id]}
                                type='text'
                                value={user?.userName || ''}
                                //autoComplete='username'
                                disabled={process?.showLoading}>
                            </input>
                        </div>
                        {errors?.userName && (
                            <div className={settings?.html?.errorText}>
                                {HtmlParser(errors?.userName)}
                            </div>
                        )}
                    </div>

                    {/* User Code Input Field */}
                    <div className='form-group mb-4'>
                        <label className='small font-weight-bold text-uppercase form-label-uppercase mb-2'>
                            {labels?.userCode?.[languageObj?.id]}
                        </label>
                        <div className='position-relative'>
                            <i className={`${icons?.fasFa?.idCard || 'fas fa-id-card'} position-absolute input-icon-left`} />
                            <input className='form-control border-0 shadow-sm form-input-icon'
                                onChange={(e) => setFormValue({ key: 'code', value: e.target.value })}
                                placeholder={labels?.userCode?.[languageObj?.id]}
                                title={labels?.userCode?.[languageObj?.id]}
                                autoComplete='username'
                                type='text'
                                value={user?.code || ''}
                                disabled={process?.showLoading}>
                            </input>
                        </div>
                    </div>

                    {/* Password Input Field */}
                    <div className='form-group mb-4'>
                        <label className='small font-weight-bold text-uppercase form-label-uppercase mb-2'>
                            {labels?.password?.[languageObj?.id]}
                        </label>
                        <div className='position-relative'>
                            <i className={`${icons?.fasFa?.eyeSlash || 'fas fa-eye-slash'} position-absolute input-icon-left`} />
                            <input className='form-control border-0 shadow-sm form-input-icon-both'
                                title={labels?.password?.[languageObj?.id]}
                                type={showPassword ? 'text' : 'password'}
                                value={user?.password || ''}
                                onChange={(e) => setFormValue({ key: 'password', value: e.target.value })}
                                placeholder={labels?.password?.[languageObj?.id]}
                                autoComplete='current-password'
                                disabled={process?.showLoading}>
                            </input>
                            <i className={`${showPassword ? (icons?.fasFa?.eye || 'fas fa-eye') : (icons?.fasFa?.eyeSlash || 'fas fa-eye-slash')} position-absolute input-icon-right`}
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ cursor: 'pointer' }} />
                        </div>
                    </div>

                </Modal.Body>

                <Modal.Footer className='border-top-0 pb-4 px-4 bg-white'>
                    <div className='d-flex justify-content-center w-100'>
                        <button className='btn btn-primary shadow-sm font-weight-bold mx-1 px-4 btn-primary'
                            data-title={labels?.save?.[languageObj?.id]}
                            disabled={process?.showLoading}
                            type='submit'>
                            {!process?.save ? (
                                <i className={`${icons?.fasFa?.save || 'fas fa-save'} mr-1`} />
                            ) : (
                                <span className={settings?.react?.html?.spinner?.borderSm} role='status' aria-hidden='true'></span>
                            )} {labels?.save?.[languageObj?.id]}
                        </button>
                        <button className='btn btn-light border shadow-sm font-weight-bold mx-1 px-4 text-secondary btn-general'
                            data-title={labels?.close?.[languageObj?.id]}
                            type='button'
                            disabled={process?.showLoading}
                            onClick={() => setDefaultValuesAsync({ isModalClose: true, subMethod: 'btnModalClose' })}>
                            <i className={`${icons?.fasFa?.arrowAltCircleLeft || 'fas fa-arrow-alt-circle-left'} mr-1`} /> {labels?.close?.[languageObj?.id]}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal>
    );
}